import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, getDuration, HintPlacement, List, OptionKey, PaginationBar, SimpleHint, Text, usePaginationParams, useResponsive } from '@common-fe/common-fe';

import { ALL_KEY } from '@/common/constants';

import { useInvestmentTabStore } from '../../stores';
import { useTransactionsHistoryQuery } from '../queries';

import TransactionHistoryHint from './TransactionHistoryHint';
import TransactionsHistoryFilters from './TransactionsHistoryFilters';
import TransactionsHistoryItem from './TransactionsHistoryItem';

// const defaultPerPage = 10;
const TransactionsHistory = () => {
  const { isMobile } = useResponsive();
  const { t } = useTranslation();
  const [currentAmount, setCurrentAmount] = useState<OptionKey>(ALL_KEY);
  const [selectedFunds, setSelectedFunds] = useState<OptionKey[]>([]);
  const [dateRange, setDateRange] = useState<Array<Date | null>>([]);
  const { page, setPage, perPage, setPerPage } = usePaginationParams();
  const { isLoading, total, isEmpty, data, isSuccess } = useTransactionsHistoryQuery({
    page,
    perPage,
    amount: currentAmount as string,
    startDate: dateRange[0] || null,
    endDate: dateRange[1] || null,
    funds: selectedFunds as string[],
  });
  const highlightedActivity = useInvestmentTabStore((state) => state.highlightedActivity);
  const setHighlightedActivity = useInvestmentTabStore((state) => state.setHighlightedActivity);
  useEffect(() => {
    if (highlightedActivity && isSuccess) {
      const targetElement = document.getElementById(highlightedActivity);
      if (targetElement) {
        targetElement.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
      setTimeout(() => {
        setHighlightedActivity('');
      }, getDuration({ seconds: 2 }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [highlightedActivity, isSuccess]);
  return (
    <Box direction="column" data-testid="transaction-history">
      <Box direction="row" align="center" margin={{ bottom: 's' }}>
        <Text size="xxlarge" weight={700}>
          {t('Investment Transaction History')}
        </Text>
        <Box margin={{ left: 'xxs' }}>
          <SimpleHint
            preferAlign={HintPlacement.LEFT}
            content={
              <Text size="small" weight={500} textAlign="center">
                {t(
                  'Investment transaction history includes all buys and sells as well as adjustments within your investment portfolio such as dividends and automatic realignments for managed investments.'
                )}
              </Text>
            }
          >
            <SimpleHint.Icon />
          </SimpleHint>
        </Box>
      </Box>

      <TransactionHistoryHint />

      <Box
        data-testid="transaction-history_wrapper"
        direction="column"
        margin={{ top: 'xxs' }}
        round="moduleRound"
        pad={isMobile ? 'spacing16' : 'spacing24'}
        background="module"
      >
        <Box
          direction="row"
          align="center"
          justify="between"
          margin={{ bottom: isMobile ? 'spacing12' : 'spacing24' }}
        >
          <Text size="large" weight={700}>
            {`${t('Transactions: ')} ${total}`}
          </Text>

          <TransactionsHistoryFilters
            selectedFunds={selectedFunds}
            onSelectFunds={setSelectedFunds}
            currentAmount={currentAmount}
            onSetCurrentAmount={setCurrentAmount}
            dateRange={dateRange}
            onSetDateRange={setDateRange}
            onSetPage={setPage}
          />
        </Box>

        {isLoading && <List.Preloader testId="transaction-history_preloader" count={2} />}
        {isEmpty && (
          <Box align="center" justify="center" pad="spacing24" background="module" round="moduleRound">
            <Text size="medium" weight={400}>
              {t('There are no activities yet')}
            </Text>
          </Box>
        )}
        {data.map((item) => (
          <TransactionsHistoryItem
            key={`${item.id}`}
            isHighlighted={item.id === highlightedActivity}
            {...item}
          />
        ))}
        <PaginationBar
          testId="transaction-history_pagination"
          page={page}
          total={total || 0}
          pageSize={perPage}
          onChangePage={setPage}
          onChangePageSize={setPerPage}
        />
      </Box>
    </Box>
  );
};
export default TransactionsHistory;
